import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type DeliveryClass, type ShippingRule } from '@hooks-api/types/dataModel/shipping'
import { useCallback, useMemo } from 'react'
import * as shippingService from '../../core/shippingService'
import { cartStoreMap, type PlatformType } from './store-map'
import { useFpB2bCalculateResultWithTransactionInfo } from './use-fp-b2b-calculate-result-with-transaction-info'

export const useFpB2bShippingService = (campaignId: number | null, campaignCode: string | undefined, platform: PlatformType, repaymentTransactionId?: string) => {
  const { useCartStore } = cartStoreMap[platform]
  const [cartValues, setCartValues] = useCartStore(campaignId)
  const { calculateResultWithTransactionInfo, paymentTransactionInfo } = useFpB2bCalculateResultWithTransactionInfo(campaignId, campaignCode, platform, repaymentTransactionId)

  const setAppliedShippingMethodId = useCallbackEvent((appliedShippingMethodId: number) => {
    setCartValues(state => ({
      ...state,
      shipping: { ...state.shipping, appliedShippingMethodId },
    }))
  })

  /**
   * 如果 paymentTransactionInfo 有值，則代表是重新結帳，則不使用 cartValues.shipping.appliedShippingMethodId
   */
  const appliedShippingMethod = useMemo(() => {
    if (paymentTransactionInfo) return paymentTransactionInfo.shipping_method
    return calculateResultWithTransactionInfo?.shipping_methods?.find(
      method => method.shipping_method_id === cartValues.shipping.appliedShippingMethodId,
    ) || calculateResultWithTransactionInfo?.shipping_methods[0]
  }, [calculateResultWithTransactionInfo?.shipping_methods, cartValues.shipping.appliedShippingMethodId, paymentTransactionInfo])

  const totalAmount = calculateResultWithTransactionInfo?.total_after_discount || 0

  const getShippingInfo = useCallback((shippingRules: ShippingRule[]) =>
    shippingService.getShippingInfo(shippingRules, totalAmount),
  [totalAmount])

  const getAppliedShippingInfo = useCallback((deliveryClass: DeliveryClass) =>
    shippingService.getShippingInfo(appliedShippingMethod?.shipping_rules[deliveryClass] || [], totalAmount),
  [appliedShippingMethod?.shipping_rules, totalAmount])

  return {
    ...cartValues.shipping,
    shippingMethods: calculateResultWithTransactionInfo?.shipping_methods || [],
    appliedShippingMethod,
    appliedShippingMethodId: appliedShippingMethod?.shipping_method_id,
    setAppliedShippingMethodId,
    getShippingInfo,
    getAppliedShippingInfo,
  }
}
